import { useState } from 'react'
import Icon from '../components/Icon'
import Toggle from '../components/Toggle'
import ConfirmDialog from '../components/ConfirmDialog'
import { SECURITY_RIGHTS } from './adminMockData'
import styles from './AdminUserRow.module.css'

const AVATAR_PALETTE = [
  '#E05A4E', '#E08A4E', '#E0C44E', '#6DBE6D',
  '#4E9EE0', '#7B6DE0', '#C44EBE', '#4EC4C4',
]

function avatarColor(id) {
  const seed = String(id)
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]
}

const STATUS_LABEL = { active: 'Active', pending: 'Pending', disabled: 'Disabled' }

const MFA_DISPLAY = {
  totp: { icon: 'lock', label: 'Authenticator app' },
  email: { icon: 'mail', label: 'Email code' },
}

/**
 * MfaStatusIcon — small glyph in its own column showing how (or whether) this
 * user has 2FA set up, so an admin can see it without opening Manage. Native
 * `title` tooltip is enough here — one short phrase, not a list.
 */
function MfaStatusIcon({ mfaMethod, mfaEnrolled }) {
  const display = mfaEnrolled ? MFA_DISPLAY[mfaMethod] : null
  return (
    <span
      className={`${styles.mfaIcon} ${display ? styles.mfaIconOn : styles.mfaIconOff}`}
      title={`Two-factor: ${display?.label ?? 'Not enrolled'}`}
    >
      <Icon name={display?.icon ?? 'lock-open'} width="16" height="16" />
    </span>
  )
}

/**
 * AdminUserRow — one row in the User Management table. Collapsed, it shows
 * identity (with an "A" badge if the user has admin_right)/email/2FA
 * status/last-login/status and a Manage button; expanded (Manage clicked),
 * it opens an inline security-rights panel with a toggle per right plus
 * Disable/Remove/Reset 2FA/Resend invite actions, and the button becomes
 * Close.
 *
 * The logged-in caller's own row (isSelf) renders too — pinned first by
 * AdminUsersTab, with an accent border and a "you" badge — but only the
 * rights toggles apply to it: Disable/Remove/Reset 2FA/Resend invite make no
 * sense against your own account, so the whole footerActions block is
 * skipped. Removing your own admin_right is intercepted one level up
 * (AdminUsersTab's handleSelfRightChange), not here — this component doesn't
 * need to know that's a special case, just that onRightChange might trigger
 * a confirmation before anything actually changes.
 *
 * Props:
 *   user {object} — see src/api/security.js's fromApiUser for the shape
 *   isSelf {boolean} — this row is the logged-in caller's own account
 *   expanded {boolean}
 *   onToggleManage {function}
 *   onRightChange {function} — (rightKey, value)
 *   onDisable {function} — omitted for isSelf
 *   onRemove {function} — omitted for isSelf
 *   canResetMfa {boolean} — caller is an admin (or super admin); Reset 2FA
 *     is hidden entirely otherwise (the backend enforces the same
 *     admin_right-or-superadmin_right gate on POST .../mfa/reset, so this
 *     isn't just a hidden button)
 *   onResetMfa {function} — async; only called when canResetMfa && user.mfaEnrolled; omitted for isSelf
 *   onResendInvite {function} — async; only called when user.status === 'pending'; omitted for isSelf
 */
export default function AdminUserRow({
  user, isSelf = false, expanded, onToggleManage, onRightChange, onDisable, onRemove, canResetMfa, onResetMfa, onResendInvite,
}) {
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [confirmResetMfa, setConfirmResetMfa] = useState(false)
  const [resettingMfa, setResettingMfa] = useState(false)
  const [resendingInvite, setResendingInvite] = useState(false)

  async function handleResetMfa() {
    setConfirmResetMfa(false)
    setResettingMfa(true)
    try {
      await onResetMfa()
    } finally {
      setResettingMfa(false)
    }
  }

  async function handleResendInvite() {
    setResendingInvite(true)
    try {
      await onResendInvite()
    } finally {
      setResendingInvite(false)
    }
  }

  return (
    <div className={`${styles.rowWrap} ${expanded ? styles.expanded : ''} ${isSelf ? styles.selfRow : ''}`}>
      <div className={styles.row}>
        <div className={styles.identity}>
          <span className={styles.avatar} style={{ backgroundColor: avatarColor(user.id) }}>
            {user.initials}
          </span>
          <span className={styles.name}>{user.name}</span>
          {user.rights.admin && (
            <span className={styles.adminBadge} title="Admin">A</span>
          )}
          {isSelf && (
            <span className={styles.selfBadge} title="This is you">
              <Icon name="user" width="12" height="12" />
              You
            </span>
          )}
        </div>
        <span className={styles.email}>{user.email}</span>
        <div className={styles.mfaCol}>
          <MfaStatusIcon mfaMethod={user.mfaMethod} mfaEnrolled={user.mfaEnrolled} />
        </div>
        <span className={styles.login}>{user.lastLogin}</span>
        <span className={`${styles.statusPill} ${styles[`status-${user.status}`]}`}>
          {STATUS_LABEL[user.status] ?? user.status}
        </span>
        <button type="button" className={styles.manageBtn} onClick={onToggleManage}>
          {expanded ? 'Close' : 'Manage'}
        </button>
      </div>

      {expanded && (
        <div className={styles.detail}>
          <span className={styles.detailHeading}>Security Rights</span>
          <p className={styles.detailSub}>Security rights are additive - each one grants a specific capability.</p>

          <div className={styles.rightsList}>
            {SECURITY_RIGHTS.map(r => (
              <div key={r.key} className={`${styles.rightCard} ${user.rights[r.key] ? styles.rightCardOn : ''}`}>
                <Toggle
                  label={r.label}
                  description={r.description}
                  tone={r.tone}
                  checked={Boolean(user.rights[r.key])}
                  onChange={value => onRightChange(r.key, value)}
                />
              </div>
            ))}
          </div>

          {!isSelf && (
          <div className={styles.detailFooter}>
            <div className={styles.footerActions}>
              {user.status === 'pending' && (
                <button type="button" className={styles.secondaryBtn} onClick={handleResendInvite} disabled={resendingInvite}>
                  {resendingInvite ? 'Sending…' : 'Resend invite'}
                </button>
              )}
              {canResetMfa && user.mfaEnrolled && (
                <button type="button" className={styles.secondaryBtn} onClick={() => setConfirmResetMfa(true)} disabled={resettingMfa}>
                  {resettingMfa ? 'Resetting…' : 'Reset 2FA'}
                </button>
              )}
              <button type="button" className={styles.disableBtn} onClick={onDisable}>
                {user.status === 'disabled' ? 'Enable user' : 'Disable user'}
              </button>
              <button type="button" className={styles.removeBtn} onClick={() => setConfirmRemove(true)}>
                Remove user
              </button>
            </div>
          </div>
          )}
        </div>
      )}

      {confirmRemove && (
        <ConfirmDialog
          title="Remove user"
          message={`Remove ${user.name} from this workspace? They will lose access immediately.`}
          confirmLabel="Remove user"
          variant="danger"
          onConfirm={() => { setConfirmRemove(false); onRemove() }}
          onCancel={() => setConfirmRemove(false)}
        />
      )}

      {confirmResetMfa && (
        <ConfirmDialog
          title="Reset two-factor authentication"
          message={`${user.name}'s current authenticator app, backup codes, and trusted devices will stop working immediately. They'll be asked to set up 2FA again on their next login, and they'll be emailed a notice — do this only if you're helping them recover access.`}
          confirmLabel="Reset 2FA"
          onConfirm={handleResetMfa}
          onCancel={() => setConfirmResetMfa(false)}
        />
      )}
    </div>
  )
}
